import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageContainer from '@shared/components/page-parts/Container';
import PageHead from '@shared/components/page-parts/Head';
import PageContent from '@shared/components/page-parts/Content';
import TopNav from '@shared/components/top-nav/Nav';
import CardsList from '@shared/components/cards-list/CardsList';
import CardItem from '@shared/components/cards-list/CardItem';

import Product from './Product';
import Controls from './Controls';

import {
  deleteProduct,
  deleteProductError,
  hideProduct,
  markProductAsDefaultContent,
  unmarkProductAsDefaultContent,
} from '@store/products/actions';
import { actions } from '@store/companies';
import { ROUTES } from '@app/routes/routesUrls';
import Breadcrumbs from '@shared/components/breadcrumbs';
import AddNew from '@shared/components/cards-list/AddNew';
import useNavigate from '@shared/router/useNavigate';
import {deleteAiProduct, renderAiProduct} from '@store/ai-product/actions';
import LimitBanner from '@shared/components/page-parts/LimitBanner';

const ProductListGrid = ({ data }) => {
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const dispatch = useDispatch();
  const apiError = useSelector((state) => state.aiProducts.error);
  const { goTo } = useNavigate();
  const { getCurrentSettings } = actions;

  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);
  const isAiProductPage = location.pathname.startsWith(`${ROUTES.AI_PRODUCT.ROOT}`);

  const handleCreateModel = () => {
    dispatch(renderAiProduct({
      name: `AI Product ${Date.now().toString(36)}${Math.random().toString(36).substring(2)}`,
      images: [],
      aspect: {
        name: 'Original',
        x: 0,
        y: 0,
      },
      scale: 0.8,
    }));
    goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.NEW}`);
  };

  const handleEditProductFactory = (item) => () => {
    const isAlphaCreated = item.images?.some(image => image?.siloSrc);
    dispatch(renderAiProduct({ ...item }));
    if (!isAlphaCreated) {
      goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.EDIT(item.id)}`);
    }
    goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.SILO(item.id)}`);
  };

  const handleHideProduct = useCallback((item) => () => {
    dispatch(hideProduct(item));
  }, []);

  const handleDeleteProductFactory = (item) => () => {
    const { id } = item;
    dispatch(deleteAiProduct(id));
  };

  const handleApiErrorClear = () => {
    dispatch(deleteProductError());
  };

  const handleShareProduct = useCallback((item) => () => {
    if (item.defaultContent) {
      dispatch(unmarkProductAsDefaultContent(item));
    } else {
      dispatch(markProductAsDefaultContent(item));
    }
  }, []);

  const breadcrumbsItems = useMemo(() => [
    { name: 'AI Products', link: ROUTES.AI_PRODUCT.ROOT },
  ], []);

  return (
    <PageContainer>
      <PageHead>
        <TopNav>
          <Breadcrumbs items={ breadcrumbsItems } />
        </TopNav>
        <LimitBanner show={ isAiProductPage && isAiProductReadOnly } />
      </PageHead>
      <PageContent>
        {data.length > 0 ? (
          <CardsList>
            { !isAiProductReadOnly && (
            <CardItem>
              <AddNew onClick={ handleCreateModel } />
            </CardItem>
            )}
            {data.map((item) => (
              <CardItem key={ item.id }>
                <Product
                  item={ item }
                  controls={ (
                    <Controls
                      apiError={ apiError }
                      onEdit={ handleEditProductFactory(item) }
                      // onCopy={ handleCopyProductFactory(item) }
                      onDelete={ handleDeleteProductFactory(item) }
                      onHide={ handleHideProduct(item) }
                      onShare={ handleShareProduct(item) }
                      onApiErrorClear={ handleApiErrorClear }
                      item={ item }
                    />
                  ) }
                  onClick={ handleEditProductFactory(item) }
                />
              </CardItem>
            ))}
          </CardsList>
        ) : (
          <CardsList>
            { !isAiProductReadOnly && (
            <CardItem>
              <AddNew onClick={ handleCreateModel } />
            </CardItem>
            )}
          </CardsList>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default ProductListGrid;
